import { CoverLetterContent } from "./coverLetter.js";
import { z } from "zod";
import { OrderRef } from "./orderBundle.js";

// ---------------------------------------------------------------------------
// 見積依頼書と写真台帳（#119、docs/56）→ 案件からの見積依頼（#218、docs/88 の1回目「依頼書を1本に」と2回目「添付」）
//   ・依頼1通＝1社の控え。案件に属し、見積は1つまで。依頼書がいつも自分の明細（RfqLine。行ごとに出どころ estimate／own）と添付（受け取った書類）を持つ
//   ・下書きは番号なし、送る瞬間に RQ-0001（再依頼は元の番号の枝番 RQ-0001-2）を採って項目・写真・添付を写して固める
//   ・状態は 下書き → 送付済 → 回答あり → 取消。期限超過は状態でなく朱の表示だけ
//   ・**売価はどの型にも持たせない**（単価・金額・仕入れ・利益の欄を作らない。紙の単価・金額の列は外注先が書く空欄）
//   ・写真台帳は field（写真）の会社だけ。3枚／頁が既定・6枚／頁も・1冊 60枚まで
//   ・画面の文は言葉のキー（{vendor}・{subject}・{site}・{period}・{trade}・{takeoff}・{project}・{vendorQuote}）を通す
// ---------------------------------------------------------------------------

const Uuid = z.string().uuid();
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const RFQ_LIMITS = {
  replyDaysMin: 1,
  replyDaysMax: 60,
  /** 写真台帳 1冊の枚数 */
  photosPerBook: 60,
  /** 1社 1日にメールで送れる通数 */
  mailPerDay: 50,
  /** メールの添付の上限（合わせて） */
  attachBytes: 10 * 1024 * 1024,
  subjectMax: 200,
  termsMax: 1000,
  periodMax: 100,
  siteMax: 200,
  mailSubjectMax: 100,
  mailBodyMax: 2000,
  cancelReasonMax: 200,
  /** 依頼書の明細の行数（#218） */
  lines: 300,
  /** 添付の件数（#218、2回目） */
  attachments: 20,
  lineNameMax: 200,
  lineSpecMax: 200,
  lineUnitMax: 20,
  lineNoteMax: 1000,
  /** 回答期限が空の依頼は、送付からこの日数で朱の表示（Figma ①。DB の fn_rfq_overdue_days と同じ） */
  noReplyDays: 14,
} as const;

export const RfqStatus = z.enum(["draft", "sent", "answered", "cancelled"]);
export type RfqStatus = z.infer<typeof RfqStatus>;
export const RFQ_STATUS_LABEL: Record<RfqStatus, string> = { draft: "下書き", sent: "送付済", answered: "回答あり", cancelled: "取消" };

/** 写真台帳の1頁の枚数 */
export const PhotosPerPage = z.union([z.literal(3), z.literal(6)]);
export type PhotosPerPage = z.infer<typeof PhotosPerPage>;

/** 項目の拾い出し（数量の内訳）の1本 */
export const RfqTakeoff = z.object({ place: z.string().nullable(), formula: z.string(), quantity: z.number().nullable(), unit: z.string() });
export type RfqTakeoff = z.infer<typeof RfqTakeoff>;

/** 依頼の項目（見積の行の写し）。単価・金額は持たない */
export const RfqItem = z.object({
  no: z.number().int(),
  kind: z.enum(["detail", "heading", "note"]),
  lineageId: Uuid.nullable(),
  name: z.string(),
  /** 規格・仕様（空で仮のメモがあればそれ） */
  spec: z.string(),
  /** 数量。0・文章だけの行は null（外注先が測る） */
  quantity: z.number().nullable(),
  unit: z.string(),
  /** 見積の行の備考 */
  note: z.string(),
  /** 場所（使う機能 places が ON のときだけ） */
  place: z.string().nullable(),
  /** {trade}（使う機能 trades が ON のときだけ） */
  trade: z.string().nullable(),
  takeoffs: z.array(RfqTakeoff),
  /** 写真台帳に入る写真の数（この項目が最初の行の写真） */
  photoCount: z.number().int(),
});
export type RfqItem = z.infer<typeof RfqItem>;

/** 写真台帳の1枚 */
export const RfqPhoto = z.object({
  /** 見積の写真（estimate_line_photos.id）。消された写真は null */
  photoId: Uuid.nullable(),
  /** 台帳の No（外した写真は null） */
  no: z.number().int().nullable(),
  itemNo: z.number().int().nullable(),
  place: z.string().nullable(),
  item: z.string(),
  extraLines: z.number().int(),
  date: Day.nullable(),
  /** 小さい絵（署名つきの短命の URL。消された写真は null） */
  thumbUrl: z.string().nullable(),
  /** 下書きで外した写真 */
  excluded: z.boolean(),
});
export type RfqPhoto = z.infer<typeof RfqPhoto>;

export const RfqVendorRef = z.object({ id: Uuid, name: z.string(), shortName: z.string() });

/** 一覧・帯の1行 */
export const RfqSummary = z.object({
  id: Uuid,
  /** 番号（枝番つき。RQ-0001-2）。下書きは null */
  no: z.string().nullable(),
  status: RfqStatus,
  vendor: RfqVendorRef,
  /** 見積（消された見積は null） */
  estimate: z.object({ id: Uuid, no: z.string().nullable(), subject: z.string() }).nullable(),
  group: z.object({ id: Uuid, name: z.string() }).nullable(),
  subject: z.string(),
  replyBy: Day.nullable(),
  /** 返事待ちで回答期限を過ぎた日数（それ以外は null） */
  overdueDays: z.number().int().nullable(),
  sentAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  /** 差し替え済（同じ見積 × 外注先の次の枝番を送った）。その番号 */
  supersededBy: z.string().nullable(),
  itemCount: z.number().int(),
  /** 明細の内訳（#218、Figma ①の「明細」の列）：見積から写した行・手で足した行の数 */
  lineCounts: z.object({ estimate: z.number().int(), own: z.number().int() }),
  /** 添付の内訳（#218、Figma ①の「添付」の列）：図面・写真・ほか */
  attachCounts: z.object({ drawing: z.number().int(), photo: z.number().int(), other: z.number().int() }),
  /** 回答（{vendorQuote}とつないだもの。いちばん新しい1件）：番号と金額（税込の合計・無ければ null） */
  answer: z.object({ vendorQuoteId: Uuid, quoteNo: z.string(), total: z.number().int().nullable() }).nullable(),
  /** 取消の依頼を出し直した依頼（再依頼・別の{vendor}へ複製）の番号。下書きは null の番号で「（下書き）」 */
  redoneBy: z.object({ id: Uuid, no: z.string().nullable() }).nullable(),
  /** この依頼（と回答）から作った{order}（#233 2a。取消を除く・下書きも）。一覧の「発注済 PO-0001」・依頼書の頭の「{order} PO-0001 ›」 */
  orders: z.array(OrderRef).default([]),
  updatedAt: z.string(),
});
export type RfqSummary = z.infer<typeof RfqSummary>;

/** 依頼書の明細の1行（#218、docs/88 §2.2）。**単価・金額・仕入れのキーを作らない** */
export const RfqLineKind = z.enum(["detail", "heading", "note"]);
export type RfqLineKind = z.infer<typeof RfqLineKind>;
export const RfqLine = z.object({
  id: Uuid,
  kind: RfqLineKind,
  /** estimate＝見積から写した行／own＝手で足した行 */
  source: z.enum(["estimate", "own"]),
  lineageId: Uuid.nullable(),
  /** 見積の画面のその行の No（見積から写した行。見積から消えた行・No の無い行は null＝「見積」とだけ出す） */
  estimateNo: z.number().int().nullable(),
  name: z.string(),
  spec: z.string(),
  quantity: z.number().nullable(),
  unit: z.string(),
  note: z.string(),
  itemId: Uuid.nullable(),
  /** 見積から写した行の場所・{trade}の名前（写した時点。画面では打たない） */
  place: z.string().nullable(),
  trade: z.string().nullable(),
});
export type RfqLine = z.infer<typeof RfqLine>;
/** PUT /rfqs/:id の明細（全部の行を並びの順に。id は画面が採る。出どころ・系譜は API が保つ＝新しい id は手で足した行） */
export const RfqLineInput = z.object({
  id: Uuid,
  kind: RfqLineKind.default("detail"),
  name: z.string().max(RFQ_LIMITS.lineNameMax).default(""),
  spec: z.string().max(RFQ_LIMITS.lineSpecMax).default(""),
  quantity: z.number().finite().min(-1e9).max(1e9).nullable().default(null),
  unit: z.string().max(RFQ_LIMITS.lineUnitMax).default(""),
  note: z.string().max(RFQ_LIMITS.lineNoteMax).default(""),
  itemId: Uuid.nullable().default(null),
});
export type RfqLineInput = z.infer<typeof RfqLineInput>;

/** 添付の1件（#218、2回目）。送るファイル：写真＝表示用（縮小版 1600px の JPEG）・ほか＝原本 */
export const RfqAttachment = z.object({
  id: Uuid,
  /** 受け取った書類 */
  documentId: Uuid,
  title: z.string(),
  kind: z.enum(["unsorted", "order", "inquiry", "invoice", "drawing", "photo", "material", "other"]),
  contentType: z.string(),
  pageCount: z.number().int().nullable(),
  /** 送るファイルの名前・大きさ（下書きは今の書類から、送ったあとは固めた値） */
  sendName: z.string(),
  sendBytes: z.number().int(),
  sendVariant: z.enum(["display", "original"]),
  /** 書類を削除した（下書きのあいだは外してから送る。送ったあとは記録として残る） */
  deleted: z.boolean(),
});
export type RfqAttachment = z.infer<typeof RfqAttachment>;

export const RfqShow = z.object({ places: z.boolean(), trades: z.boolean(), takeoff: z.boolean(), photos: z.boolean() });
export type RfqShow = z.infer<typeof RfqShow>;

/** 依頼書の画面の中身（GET /rfqs/:id） */
export const Rfq = RfqSummary.extend({
  /** 宛先の担当者。送ったあとは送った時の名前 */
  contact: z.object({ id: Uuid.nullable(), name: z.string(), department: z.string(), email: z.string().nullable() }).nullable(),
  /** 選べる担当者（下書きだけ） */
  contacts: z.array(z.object({ id: Uuid, name: z.string(), department: z.string(), email: z.string().nullable(), primary: z.boolean() })),
  siteAddress: z.string(),
  workPeriodText: z.string(),
  terms: z.string(),
  includePhotos: z.boolean(),
  photosPerPage: PhotosPerPage,
  includeTakeoffs: z.boolean(),
  /** 出し分け（使う機能・field）。送ったあとは送った時のもの */
  show: RfqShow,
  items: z.array(RfqItem),
  /** 写真台帳（field の会社だけ。無ければ空） */
  photos: z.object({ total: z.number().int(), included: z.number().int(), overLimit: z.number().int(), list: z.array(RfqPhoto) }),
  sentVia: z.enum(["email", "print"]).nullable(),
  deliveries: z.array(z.object({ at: z.string(), to: z.string(), bccSelf: z.boolean(), by: z.string(), bytes: z.number().int() })),
  responses: z.array(z.object({ at: z.string(), kind: z.enum(["structured", "ocr", "manual_entry"]), vendorQuoteId: Uuid.nullable() })),
  cancelReason: z.string(),
  /** 工事の見積期間の目安（建設業法20条3項・施行令5条の9。field の会社だけ。日数だけで金額は持たない） */
  legalDays: z.union([z.literal(1), z.literal(10), z.literal(15)]).nullable(),
  /** 下書きを修正できるか（送ったあとは false） */
  canEdit: z.boolean(),
  /** 案件（#218。見積が消えた古い依頼は null） */
  project: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** 明細（#218。下書きは修正できる・送ったあとは送った時点のもの） */
  lines: z.array(RfqLine),
  /** 添付（#218、2回目）と、送るファイルの合計（PDF を除く） */
  attachments: z.array(RfqAttachment),
  attachBytes: z.number().int(),
  /** 依頼日（Figma ②。null＝送る日。送ったあとは固めた日） */
  requestDate: Day.nullable(),
  /** 担当者（紙の「担当」） */
  staff: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** 選べる担当者（下書きだけ。会社の利用できる人） */
  staffOptions: z.array(z.object({ id: Uuid, name: z.string() })),
  /** 紙に回答記入欄・条件欄を付ける（Figma ②の送付の段） */
  printAnswerBox: z.boolean(),
  printTerms: z.boolean(),
  /** 再依頼の元・複製の元 */
  previous: z.object({ id: Uuid, no: z.string().nullable() }).nullable(),
  copiedFrom: z.object({ id: Uuid, no: z.string().nullable() }).nullable(),
  /** メールの窓の既定（差し込みを埋めた件名・本文と宛先の候補）。取消は null */
  /** mode＝渡し方の既定（#225：{vendor}ごとの既定 → 添付） */
  mail: z.object({ subject: z.string(), body: z.string(), to: z.string().nullable(), mode: z.enum(["page", "attach"]).optional() }).nullable(),
});
export type Rfq = z.infer<typeof Rfq>;

/** PUT /rfqs/:id（下書きだけ。省いた欄は変えない）。version（GET の updatedAt）が違えば 409 RFQ_STALE（#218、#202 と同じ） */
export const RfqUpdate = z.object({
  version: z.string().min(1),
  /** {vendor}を変える（下書きだけ。変えると先方担当者は既定に戻る） */
  vendorId: Uuid.optional(),
  contactId: Uuid.nullable().optional(),
  subject: z.string().trim().max(RFQ_LIMITS.subjectMax).optional(),
  siteAddress: z.string().trim().max(RFQ_LIMITS.siteMax).optional(),
  workPeriodText: z.string().trim().max(RFQ_LIMITS.periodMax).optional(),
  replyBy: Day.nullable().optional(),
  requestDate: Day.nullable().optional(),
  staffId: Uuid.nullable().optional(),
  terms: z.string().max(RFQ_LIMITS.termsMax).optional(),
  includePhotos: z.boolean().optional(),
  photosPerPage: PhotosPerPage.optional(),
  includeTakeoffs: z.boolean().optional(),
  photoExclude: z.array(Uuid).max(500).optional(),
  printAnswerBox: z.boolean().optional(),
  printTerms: z.boolean().optional(),
  /** 明細（全部の行を並びの順に） */
  lines: z.array(RfqLineInput).max(RFQ_LIMITS.lines).optional(),
  /** 添付（受け取った書類の id を並びの順に。2回目） */
  attachmentIds: z.array(Uuid).max(RFQ_LIMITS.attachments).optional(),
});
export type RfqUpdate = z.infer<typeof RfqUpdate>;

export const RfqCreate = z.object({ vendorId: Uuid });
/** POST /estimates/:id/rfq-lines（見積の行の「見積依頼に追加」）：rfqId（その{vendor}の下書き）に写す。無ければ新しい依頼 */
export const RfqLinesAdd = z.object({ vendorId: Uuid, lineageIds: z.array(Uuid).min(1).max(RFQ_LIMITS.lines), rfqId: Uuid.optional() });
/**
 * DELETE /estimates/:id/rfq-lines（見積の行の「見積依頼解除」。#270、docs/88 §4.5）：その見積 × その{vendor}の下書きから、系譜の合う明細を外す。
 * 明細が 0 になり添付も無い下書きは削除。送付済・回答ありは触らない（控え）
 */
export const RfqLinesRemove = z.object({ vendorId: Uuid, lineageIds: z.array(Uuid).min(1).max(RFQ_LIMITS.lines) });
export type RfqLinesRemove = z.infer<typeof RfqLinesRemove>;
/** DELETE /estimates/:id/rfq-lines の答え：外した明細の数・削除した下書き */
export const RfqLinesRemoved = z.object({ removed: z.number().int().nonnegative(), deletedRfqIds: z.array(Uuid) });
export type RfqLinesRemoved = z.infer<typeof RfqLinesRemoved>;
/** POST /rfqs/:id/import（見積の明細から取込） */
export const RfqImportRequest = z.object({ version: z.string().min(1), estimateId: Uuid, lineageIds: z.array(Uuid).min(1).max(RFQ_LIMITS.lines), afterLineId: Uuid.nullable().optional() });
/** POST /rfqs/:id/copy（別の{vendor}へ複製） */
export const RfqCopyRequest = z.object({ vendorId: Uuid });

/** GET /rfqs/:id/estimate-lines（取込の窓）。**単価・金額・仕入れのキーを返さない** */
export const RfqEstimateLines = z.object({
  /** 依頼に見積が付いている（その見積に決まる） */
  fixed: z.boolean(),
  estimateId: Uuid.nullable(),
  estimates: z.array(z.object({ id: Uuid, no: z.string().nullable(), subject: z.string() })),
  sheets: z.array(
    z.object({
      name: z.string(),
      lines: z.array(
        z.object({
          lineageId: Uuid,
          no: z.number().int().nullable(),
          kind: RfqLineKind,
          name: z.string(),
          spec: z.string(),
          quantity: z.number().nullable(),
          unit: z.string(),
          vendor: z.object({ id: Uuid, shortName: z.string() }).nullable(),
          /** この依頼に写してある（同じ系譜） */
          imported: z.boolean(),
        }),
      ),
    }),
  ),
});
export type RfqEstimateLines = z.infer<typeof RfqEstimateLines>;
export const RfqIssueRequest = z.object({ via: z.literal("print") });
export const RfqSendRequest = z.object({
  to: z.string().trim().email("メールアドレスの形を確かめてください").max(254),
  bccSelf: z.boolean().default(true),
  subject: z.string().trim().min(1, "件名を入れてください").max(RFQ_LIMITS.subjectMax),
  body: z.string().trim().min(1, "本文を入れてください").max(4000),
  /** 送付状（#223、docs/89 §5.2）：付けると添付の PDF の1枚目が送付状。メールでは既定オフ（null・省く＝付けない） */
  cover: CoverLetterContent.nullable().optional(),
  /** 渡し方（#225、docs/91 §3.8）：page＝閲覧ページ／attach＝添付。省けば{vendor}ごとの既定 → 添付（今と同じ） */
  mailMode: z.enum(["page", "attach"]).optional(),
  /** 閲覧ページの公開期限（日。1〜60・既定 45） */
  expiresDays: z.number().int().min(1).max(60).optional(),
});
export type RfqSendRequest = z.infer<typeof RfqSendRequest>;
export const RfqCancelRequest = z.object({ reason: z.string().trim().max(RFQ_LIMITS.cancelReasonMax).optional() });
export const RfqAnswerRequest = z.object({ vendorQuoteId: Uuid.optional() });

/** 見積の行のしるし（系譜ごと）。unsent＝外注先は付いたが送っていない */
export const RfqLineMark = z.object({
  lineageId: Uuid,
  vendorId: Uuid,
  rfqId: Uuid.nullable(),
  state: z.enum(["unsent", "sent", "answered"]),
  no: z.string().nullable(),
  replyBy: Day.nullable(),
  answeredAt: z.string().nullable(),
  overdueDays: z.number().int().nullable(),
});
export type RfqLineMark = z.infer<typeof RfqLineMark>;

/** GET /estimates/:id/rfqs：外注先ごとのまとめ（帯）・依頼の一覧・行のしるし */
export const EstimateRfqs = z.object({
  vendors: z.array(z.object({ vendor: RfqVendorRef, lineCount: z.number().int(), current: RfqSummary.nullable() })),
  rfqs: z.array(RfqSummary),
  marks: z.array(RfqLineMark),
  /** 未回答の数 */
  waiting: z.number().int(),
});
export type EstimateRfqs = z.infer<typeof EstimateRfqs>;

/** ordered＝発注済（#233 2a。{order}を作った依頼＝状態の列は足さない） */
export const RfqListFilter = z.enum(["waiting", "answered", "draft", "cancelled", "ordered", "all"]);
export type RfqListFilter = z.infer<typeof RfqListFilter>;
export const RfqList = z.object({
  items: z.array(RfqSummary),
  counts: z.object({ waiting: z.number().int(), answered: z.number().int(), draft: z.number().int(), cancelled: z.number().int(), ordered: z.number().int(), all: z.number().int() }),
});
export type RfqList = z.infer<typeof RfqList>;

/** ホームの「{vendor}の返事待ち」 */
export const HomeRfqWaiting = z.object({ count: z.number().int(), overdue: z.number().int(), nextReplyBy: Day.nullable() });
export type HomeRfqWaiting = z.infer<typeof HomeRfqWaiting>;

// ---------------------------------------------------------------------------
// 設定「見積依頼」（tenants.rfq_settings。NULL・省いたキー＝製品の既定 PRODUCTS[*].rfq）
// ---------------------------------------------------------------------------
export const RfqDefaults = z.object({
  /** 回答期限の既定（送る日から n日）。null＝空欄（#218：既定は空欄） */
  replyDays: z.number().int().min(RFQ_LIMITS.replyDaysMin).max(RFQ_LIMITS.replyDaysMax).nullable(),
  terms: z.string().max(RFQ_LIMITS.termsMax),
  mailSubject: z.string().trim().min(1, "件名を入れてください").max(RFQ_LIMITS.mailSubjectMax),
  mailBody: z.string().trim().min(1, "本文を入れてください").max(RFQ_LIMITS.mailBodyMax),
});
export type RfqDefaults = z.infer<typeof RfqDefaults>;
export const RfqSettings = RfqDefaults.extend({ source: z.enum(["product", "tenant"]), productDefaults: RfqDefaults });
export type RfqSettings = z.infer<typeof RfqSettings>;
/** PUT /company/rfq-settings。null＝製品の既定に戻す */
export const RfqSettingsUpdate = RfqDefaults.nullable();
export type RfqSettingsUpdate = z.infer<typeof RfqSettingsUpdate>;

/** メールの差し込み（設定の画面で押すと欄に入る）。言葉の差し込み {vendor} と混ざらないよう全角の角括弧 */
export const RFQ_MAIL_FIELDS = ["［宛名］", "［番号］", "［件名］", "［回答期限］", "［自社名］", "［担当者］"] as const;
export type RfqMailValues = { to: string; no: string; subject: string; replyBy: string; company: string; staff: string };
/**
 * 下書きのメールの窓で［番号］に入れておく文字。番号は送る瞬間に採るので、API が採った番号に置き換えてから送る
 * （10/7 本番の確かめで、件名・本文に「（送るときに付きます）」のまま届いたのを直した）
 */
export const RFQ_NO_PENDING = "（送るときに付きます）";
/** メールの件名・本文の RFQ_NO_PENDING を採った番号に置き換える */
export const fillRfqNo = (text: string, no: string): string => text.replaceAll(RFQ_NO_PENDING, no);
export function fillRfqMail(text: string, v: RfqMailValues): string {
  return text
    .replaceAll("［宛名］", v.to)
    .replaceAll("［番号］", v.no)
    .replaceAll("［件名］", v.subject)
    .replaceAll("［回答期限］", v.replyBy)
    .replaceAll("［自社名］", v.company)
    .replaceAll("［担当者］", v.staff);
}

/** 回答期限の既定：今日（YYYY-MM-DD）＋日数。日数が空（null）なら空欄（#218） */
export function rfqReplyDate(today: string, days: number | null): string | null {
  if (days == null) return null;
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** 「2.4MB」「420KB」（添付の大きさ。1000 でなく 1024 で割る） */
export function rfqSizeText(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

/** 添付の目盛り（②改、10/20）：「2.0 ／ 10 MB」。0.1MB 未満は「12 KB ／ 10 MB」 */
export function rfqAttachMeter(bytes: number, limit: number = RFQ_LIMITS.attachBytes): string {
  const MB = 1024 * 1024;
  const lim = `${Number.isInteger(limit / MB) ? limit / MB : (limit / MB).toFixed(1)} MB`;
  if (bytes >= MB / 10) return `${(bytes / MB).toFixed(1)} ／ ${lim}`;
  return `${Math.max(bytes > 0 ? 1 : 0, Math.round(bytes / 1024))} KB ／ ${lim}`;
}

/** 「2026年10月13日（火）」 */
export function rfqDateLong(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const w = "日月火水木金土"[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${y}年${m}月${d}日（${w}）`;
}

/**
 * 工事の見積期間の目安（建設業法20条3項・施行令5条の9。2025-12-12 改正の前は 20条4項・施行令6条。金額と日数は同じ）：
 * 予定価格 500万円未満＝1日以上／500万円以上 5,000万円未満＝10日以上／5,000万円以上＝15日以上。
 * 予定価格は画面に無いので、依頼する行の見積の金額（税抜）の合計を目安にする（多めに出る＝慎重な側）
 */
export function rfqLegalDays(amountExTax: number): 1 | 10 | 15 {
  if (amountExTax >= 50_000_000) return 15;
  if (amountExTax >= 5_000_000) return 10;
  return 1;
}

/** 二次元コードの中身（docs/56 §3.2）：GB1/RQ/{番号}/{ハッシュの頭 16 バイトの base32} */
export function rfqQrText(no: string, hash: Uint8Array): string {
  const A = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const bytes = hash.slice(0, 16);
  let bits = 0;
  let value = 0;
  let out = "";
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += A[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += A[(value << (5 - bits)) & 31];
  return `GB1/RQ/${no}/${out}`;
}

export const RFQ_MSG = {
  title: "見積依頼書",
  feature: "{vendor}への見積依頼",
  listTitle: "見積依頼",
  notFound: "見積依頼が見つかりません",
  estimateNotFound: "見積が見つかりません",
  vendorNotFound: "{vendor}が見つかりません",
  frozen: "送付済の見積依頼は修正できません。「再依頼」で新しく作成します",
  /** #275：削除できるのは下書きと取消した見積依頼だけ（送付済・回答ありは先に取消） */
  notDeletable: "送付済の見積依頼は削除できません（先に取消にします）",
  /** #275：{order}の元になった見積依頼（order_line_sources が指す）は完全に削除できない（DB の RFQ_PURGE_ORDERED） */
  purgeOrdered: "{order}の元になった見積依頼書は削除できません",
  empty: "この{vendor}の行がありません。見積の行の「…」→「見積依頼に追加」で追加します",
  noEstimate: "見積が削除されています",
  draftExists: "この{vendor}の下書きがあります",
  statusInvalid: "その状態には変えられません",
  vendorQuoteInvalid: "その{vendorQuote}はこの{vendor}のものではありません",
  mailLimit: "見積依頼のメールは1日 {n}通までです。印刷・FAX で送るか、明日送ってください",
  tooLarge: "添付が {size} です（{limit} まで）。写真台帳を6枚／頁にする・写真を減らす・印刷で送る のどれかにしてください",
  companyRequired: "自社情報の会社名を入力してから送付してください",
  photosOver: "写真が {total}枚あります。{limit}枚まで添付します",
  legalDays: "見積期間は {days}日以上が目安です（建設業法）",
  sent: "{no} を送りました",
  issued: "{no} を送付済にしました",
  answered: "回答ありにしました",
  unanswered: "未回答に戻しました",
  cancelled: "取消にしました",
  rerequested: "再依頼の下書きを作成しました",
  deleted: "下書きを削除しました",
  hasRfq: "{vendor}に送った見積依頼が {n}件あります。取消して削除しますか",
  settingsTitle: "見積依頼",
  // 行のしるし（docs/56 §6.1）
  markUnsent: "未送付",
  markSent: "依頼済",
  markOverdue: "期限超過",
  markAnswered: "回答あり",
  markReply: "回答",
  supersededBy: "{no} で差し替え",
  overdue: "期限超過 {n}日",
  // 画面（docs/56 §6）
  cardTitle: "{vendor}の未回答",
  cardNext: "次の回答期限 {d}",
  cardOverdue: "期限超過 {n}件",
  open: "見積依頼書",
  menuCreate: "見積依頼書作成",
  menuLedger: "写真台帳（PDF）",
  /** 「{vendorQuote}」の帯の空の形のボタン・{vendor}の付いた行が無いときの窓の題（10/7 射場さん：入口を「≡」と帯に） */
  create: "見積依頼書作成",
  pickVendor: "{vendor}を選ぶ",
  /** 窓で{vendor}と行を選ぶ（選んだ行に{vendor}が付く＝行の「見積依頼に入れる…」と同じ） */
  pickLines: "依頼する行",
  pickAll: "すべて選択",
  pickNone: "選択解除",
  pickNoLines: "依頼する行がありません。明細を書いてから作成します",
  saveFailed: "見積を保存できませんでした",
  noVendorLines: "{vendor}を設定した行がありません。行の「…」→「見積依頼に追加」で設定します",
  ledgerTitle: "写真台帳",
  ledgerBook: "{n}冊目（{a}〜{b}）",
  perPage3: "3枚／頁",
  perPage6: "6枚／頁",
  sendMail: "メールで送信",
  sendMailDo: "メールで送信",
  sendPrint: "印刷・FAX で送付",
  /** 送付の画面（/rfqs/:id/send、2026-10-21 射場さんの決め＝docs/89 ★16・docs/88 §16）の主のボタン。前の窓の「送付済にして PDF を表示」と「番号を採って…」の1行はやめた */
  printDo: "送付済にして印刷",
  /** 送付の画面の脇のボタン（番号を採って送付済にし、PDF を保存する） */
  printSave: "PDF 保存",
  printSaveTitle: "番号を採って送付済にし、PDF をこの端末に保存します",
  /** 送付の画面の題（開いたボタン「送付」と同じ言葉） */
  sendPageTitle: "送付",
  /** 送付の画面の紙の透かし（送る前の確認。番号は付いていない） */
  wmConfirm: "確認用",
  pdfOpen: "PDF 表示",
  /** 下書きの紙を見る（#149：見積書の「≡」と同じ文言）。押すと書きかけを保存してから紙の画面 /rfqs/:id/paper */
  pdfDraft: "PDF で確認（透かし入り）",
  /** 使う機能で{vendorQuote}が OFF・見積依頼が ON の会社の帯（#149） */
  bandCount: "見積依頼 {n}件",
  /** 送付済・回答ありの依頼のスイッチの横の言葉（S-00009：「回答ありにする」「返事待ちに戻す」の2つのボタンから。ON＝回答あり・OFF＝返事待ち） */
  answer: "回答あり",
  rerequest: "再依頼",
  cancel: "取消",
  cancelAsk: "この見積依頼を取消にします。番号と PDF は残ります。",
  cancelReason: "取消の理由（任意）",
  deleteDraft: "下書き削除",
  deleteAsk: "この下書きを削除しますか",
  /** #275（docs/99 §5、Figma 292:228）：取消した見積依頼の「⋯」のいちばん下（朱）と確認の窓。見積書のゴミ箱の「完全に削除」と同じ形 */
  purge: "削除",
  purgeAsk: "見積依頼書「{no}」を完全に削除します。",
  purgeNote: "番号 {no} は欠番になり、PDF と送付の控えも削除します。元に戻せません。",
  purgeAnswer: "回答の{vendorQuote} {no} は残ります（依頼との紐づけだけ外れます）。",
  purged: "{no} を削除しました",
  cardTo: "宛先",
  cardBody: "依頼の中身",
  cardItems: "項目",
  cardAttach: "添付",
  cardSend: "送る",
  cardHistory: "送った記録・回答",
  contact: "担当者",
  contactNone: "指定しない",
  replyBy: "回答期限",
  terms: "条件",
  itemsEdit: "見積で修正",
  /** 送信設定の「添付」の下のスイッチ（#220：題の下に置くので名詞だけ） */
  includePhotos: "写真台帳",
  includeTakeoffs: "{takeoff}",
  photoExcluded: "外した写真",
  noPhotos: "この{vendor}の行に写真はありません",
  mailTo: "宛先のメールアドレス",
  mailBcc: "控え Bcc",
  mailSubject: "件名",
  mailBody: "本文",
  mailAttach: "添付",
  mailAttachLater: "見積依頼書（PDF。送付するときに作成します）",
  sentByMail: "メール",
  sentByPrint: "印刷・FAX",
  responseManual: "回答ありにした",
  responseVendorQuote: "{vendorQuote}を登録した",
  tabWaiting: "未回答",
  tabAnswered: "回答あり",
  tabDraft: "下書き",
  tabCancelled: "取消",
  tabAll: "すべて",
  colNo: "番号",
  colReplyBy: "回答期限",
  colSent: "送った日",
  colStatus: "状態",
  listEmpty: "見積依頼はありません",
  settingsHelp: "{vendor}に送る見積依頼書の既定です。変更しても、作成済みの下書きは変わりません。",
  replyDaysLabel: "回答期限（作成日から）",
  /** #218：空欄が既定 */
  replyDaysHelp: "任意です。未入力の場合、新しい見積依頼の回答期限は空欄になり、帳票とメールには記載されません。日数を入力すると、作成した日からその日数後が回答期限の既定になります。",
  termsLabel: "条件の既定文",
  mailSubjectLabel: "メール件名",
  mailBodyLabel: "メール本文",
  mailFields: "差し込み",
  settingsReset: "製品既定に復元",
  /** 製品の既定に戻したとき（S-00008 の続き：保存は自動になったので「保存しました」とは別の文） */
  settingsResetDone: "見積依頼の既定を製品の既定に戻しました",
  /** 回答期限の範囲の外（送らずに下に朱の1行） */
  replyDaysRange: "回答期限は 1〜60日で入力してください",
  settingsIsDefault: "製品の既定",
  phoneFilter: "{vendor}で絞り込み",
  phoneFilterAll: "すべて",
  phoneFilterNone: "{vendor}なし",
  cancelAndDelete: "取消して削除",
  // ---- #218（docs/88）：案件のタブ・作成画面・取込・添付・送付 ----
  /** 送る前の No.（見積書の「未発行」と同じ） */
  noPending: "未発行",
  /** 一覧の下書きの番号の欄 */
  draftNo: "（下書き）",
  groupNotFound: "{project}が見つかりません",
  groupHasRfq: "見積依頼のある{project}は削除できません。見積依頼を取消・削除してから削除してください",
  stale: "他の利用者が変更しました。再読込してください",
  lineLimit: "見積依頼の明細は 300 行までです",
  lineageInvalid: "その行はこの見積依頼の見積の行ではありません",
  documentMismatch: "この見積依頼には別の見積が紐づいています",
  groupMismatch: "その見積は別の{project}のものです",
  attachInvalid: "添付できるのは同じ{project}の受け取った書類（ファイル）だけです",
  attachLimit: "添付は 20 件までです",
  attachGone: "削除した書類が添付に残っています。外してから送付してください",
  /** メールの添付の上限（PDF 込み 10MB） */
  attachOver: "添付の容量が上限を超えています（合計 {size}／上限 {limit}）",
  // 案件のタブ
  colVendor: "{vendor}",
  colSubject: "件名",
  colSentAt: "送付日",
  colLines: "明細",
  colAttach: "添付",
  colAnswer: "回答",
  lineCount: "{n}行",
  lineSplitEstimate: "見積 {n}",
  lineSplitOwn: "追加 {n}",
  attachOnly: "添付のみ",
  attachDrawing: "図面 {n}",
  attachPhoto: "写真 {n}",
  attachOther: "資料 {n}",
  notSent: "未送付",
  noAnswer: "未回答",
  sinceSent: "送付から {n}日",
  redone: "{no} で再依頼",
  importAnswer: "見積で取込",
  listHelp: "回答期限を過ぎた依頼と、回答期限が空で送付から 14 日たった依頼は朱の表示になります。依頼書の明細は送った時点の控えです。見積の行を修正しても変わりません。",
  createTitle: "見積依頼書作成",
  createDo: "作成",
  rowOpen: "開く",
  // 作成画面
  vendorLabel: "{vendor}",
  subjectLabel: "件名",
  requestDate: "依頼日",
  staff: "担当者",
  staffNone: "指定しない",
  replyByHelp: "任意です。未入力の場合、帳票とメールには記載されません。",
  estimateLink: "見積",
  send: "送付",
  registerAnswer: "回答登録",
  copy: "別の{vendor}へ複製",
  copyTitle: "別の{vendor}へ複製",
  copyDo: "複製",
  /** 別の{vendor}へ複製で、元と同じ{vendor}を選んだ（#288 の取引先検索から） */
  copySameVendor: "元と同じ{vendor}です",
  copied: "{vendor}への下書きを作成しました",
  details: "詳細",
  importLines: "見積明細から取込",
  addLine: "＋ 行追加",
  addHeading: "見出し追加",
  lineSummary: "{n} 行　見積から {a}・追加 {b}",
  colName: "{lineName}",
  colSpec: "{lineSpec}",
  colQty: "数量",
  colUnit: "単位",
  colPrice: "単価",
  colAmount: "金額",
  colNote: "{lineNote}",
  colOrigin: "元",
  answerCell: "回答欄",
  originEstimate: "見積 行 {n}",
  originEstimateOnly: "見積",
  originOwn: "追加",
  originTitle: "{no} から写した行",
  termsBox: "条件・依頼文",
  /** 条件・依頼文の1行（#220）：押すと数行に広がる */
  termsExpand: "広げる",
  termsCollapse: "閉じる",
  linesEmpty: "明細はありません（0行でも送付できます）",
  lineMenu: "{name}の操作",
  lineAddBelow: "下に行追加",
  lineUp: "上へ",
  lineDown: "下へ",
  lineDelete: "削除",
  headingPlaceholder: "見出し",
  // 取込の窓
  importTitle: "見積明細から取込",
  importDo: "取込",
  importEstimate: "見積",
  importedTag: "取込済",
  importNone: "取り込める行がありません",
  importDone: "{n}行を取り込みました",
  importVendorCol: "{vendor}",
  // 添付の段（2回目）
  attachTitle: "添付",
  attachPick: "書類から選択",
  attachAdd: "ファイル追加",
  attachPickTitle: "書類から選択",
  attachPickDo: "決定",
  attachPickEmpty: "選べる書類がありません",
  attachRemove: "{name}を外す",
  attachOriginal: "{kind}・原本のまま",
  attachDisplay: "写真・縮小版 1600px",
  attachTotal: "添付の合計 {meter}",
  attachHelp: "添付した書類は{project}の書類にも保存されます。写真は縮小版（長辺 1600px）、図面ほかは原本のまま送付します。",
  attachDeleted: "削除済",
  attachPrintNote: "添付 {n}件は印刷されません",
  attachKindPdf: "PDF",
  attachKindPhoto: "写真",
  attachKindImage: "画像",
  // 送信設定（#220、Figma ②改2：添付の帯の右の要約を押すと帯の下に1段広がる。中身は 送信先／送信方法／添付／印字項目）
  sendTitle: "送付",
  sendTo: "送信先",
  sendSettings: "送信設定",
  sendVia: "送信方法",
  printGroup: "印字項目",
  /** オン＝送信方法・添付・印字項目をこの端末に覚え、次に作る見積依頼書にも使う */
  rememberSettings: "次回も同じ設定",
  /** 要約の「控え Bcc」 */
  summaryBcc: "Bcc",
  // 「送付」を押したときの確認（短い1枚。設定の変更は帯で）
  sendDo: "送信",
  confirmAttach: "添付 {n}件",
  confirmNoAttach: "添付なし",
  confirmLedgerOn: "写真台帳あり",
  confirmLedgerOff: "写真台帳なし",
  /** 確認の窓の中で件名・本文を開く口 */
  mailBodyToggle: "メール本文",
  /** ②改（10/20）：見出しの題の右（番号の無い下書き）・保存の様子・道具の行の右の小さな文 */
  autoSaved: "自動保存 {at}",
  saving: "保存中…",
  mailToOther: "ほかのアドレスを入力",
  sending: "送信中…",
  viaMail: "メール",
  /** #223（docs/89 §5.2）：「郵送」を使わない。2026-10-22：「印刷・FAX」→「印　刷」（ボタンの字。間は全角の空白） */
  viaPrint: "印　刷",
  /** 送信設定の要約の字（2026-10-22。ボタンでないので空白なし） */
  viaPrintText: "印刷",
  bccSelf: "控え Bcc",
  printAnswerBox: "回答記入欄",
  printTerms: "条件欄",
  // 見積書の画面
  menuAddLine: "見積依頼に追加",
  addLineTitle: "見積依頼に追加",
  addLineTarget: "追加する見積依頼",
  addLineNew: "新しい見積依頼",
  addLineDo: "追加",
  addLineDone: "見積依頼に追加しました",
  /** 行の「…」：外注先が付いて未送付の行だけ（#270）。押すと行の外注先を外し、その{vendor}の下書きからも外す */
  menuRemoveLine: "見積依頼解除",
  markMore: "ほか {n}社",
} as const;
